import useSmsCreditAccess from "@/modules/smsCredit/useSmsCreditAccess";
import { HeroBand, HeroCard, HeroError, HeroSkeleton, HeroStat, HeroStats } from "@/modules/mobileHome/HeroCard";
import SalonHeader from "@/modules/mobileHome/SalonHeader";
import ShortcutEditor from "@/modules/mobileHome/ShortcutEditor";
import ShortcutGrid from "@/modules/mobileHome/ShortcutGrid";
import Suggestions from "@/modules/mobileHome/Suggestions";
import UpcomingAppointments, { UpcomingEmpty } from "@/modules/mobileHome/UpcomingAppointments";
import { compactRial, faNumber, isoDate, moneyNote, percentOf, rialText } from "@/modules/mobileHome/format";
import classes from "@/modules/mobileHome/home.module.scss";
import { sectionsFor } from "@/modules/mobileHome/sections";
import { audienceSuggestion, managerSuggestions, unpaidSuggestions } from "@/modules/mobileHome/suggestionList";
import { upcomingFromCalendar } from "@/modules/mobileHome/upcoming";
import useHomeShortcuts from "@/modules/mobileHome/useHomeShortcuts";
import useSectionBadges from "@/modules/mobileHome/useSectionBadges";
import useDashboardOverview from "@/pages/dashboard/overview/useDashboardOverview";
import { useGetDailyAppointment } from "@/services/appointment/hooks/useGetDailyAppointment";
import { dateRange } from "@/services/barberDashboard/utils";
import { useReviewAccess } from "@/services/reviews/useReviewAccess";
import { useSmartAudiences } from "@/services/smartAudience/hooks";
import { useDisclosure } from "@mantine/hooks";
import { IconMessage, IconPlus } from "@tabler/icons-react";
import { ReactNode, useMemo } from "react";
import { Link } from "react-router-dom";
import useGetWallet from "./hooks/useGetWalletInfo";

// `top` replaces the salon header (ManagerHome adds the home view switch under it).
function Home({ top }: { top?: ReactNode }) {
  const { canRead: canReadReviews } = useReviewAccess();
  const { allowed: canUseSmsCredit } = useSmsCreditAccess();

  // Today only (the API end date is exclusive), so capacity describes today as well.
  const range = useMemo(() => dateRange(1), []);
  const overview = useDashboardOverview(range);
  const wallet = useGetWallet();
  const today = useMemo(() => isoDate(new Date()), []);
  const appointments = useGetDailyAppointment({ fromDate: today, toDate: today });

  const sections = useMemo(() => sectionsFor("manager", canReadReviews), [canReadReviews]);
  const { shortcuts, save, reset } = useHomeShortcuts("manager", sections);
  const badges = useSectionBadges({ onlineBookings: true, pendingReviews: canReadReviews });
  // The drawer needs stable open/close handlers (it re-subscribes whenever close changes).
  const [editing, { open: openEditor, close: closeEditor }] = useDisclosure(false);

  const { upcoming, visitsToday } = useMemo(() => upcomingFromCalendar(appointments.data), [appointments.data]);
  const later = upcoming.length - 3;

  // The three most important ready-made SMS audiences, then unpaid bookings. Where the API is missing
  // (production lags dev), the older hints take their place.
  const audiences = useSmartAudiences({ take: 3 });
  const opportunities = useMemo(() => {
    if (audiences.isLoading) return [];
    if (audiences.isError) return managerSuggestions(overview.data?.opportunities);
    return [
      ...(audiences.data ?? []).map((audience) => audienceSuggestion(audience)),
      ...unpaidSuggestions(overview.data?.opportunities),
    ];
  }, [audiences.isLoading, audiences.isError, audiences.data, overview.data]);

  const headline = overview.data?.headline;
  const month = compactRial(headline?.monthSales?.value);

  return (
    <div className={classes.page}>
      {top ?? <SalonHeader />}

      {overview.isLoading ? (
        <HeroSkeleton label="خلاصه‌ی امروز" />
      ) : overview.isError || !headline ? (
        <HeroError label="خلاصه‌ی امروز" onRetry={() => void overview.refetch()} />
      ) : (
        <HeroCard
          ariaLabel="خلاصه‌ی امروز"
          label="فروش امروز"
          amount={headline.todaySales?.value}
          note={moneyNote(headline.todaySales, "هنوز فروشی ثبت نشده")}
          link={{ to: "/dashboard/overview", label: "گزارش کامل" }}
        >
          <HeroStats>
            <HeroStat
              label="نوبت‌های امروز"
              value={faNumber(headline.todayAppointments?.completed)}
              suffix={`از ${faNumber(headline.todayAppointments?.total)}`}
              progress={percentOf(headline.todayAppointments?.completed ?? 0, headline.todayAppointments?.total ?? 0)}
            />
            <HeroStat
              label="ظرفیت پرشده"
              value={headline.capacity?.hasSchedule ? `${faNumber(headline.capacity.fillRatePercent)}٪` : "—"}
              progress={headline.capacity?.hasSchedule ? headline.capacity.fillRatePercent : undefined}
              hint={headline.capacity?.hasSchedule ? undefined : "شیفتی ثبت نشده"}
            />
            <HeroStat label="فروش این ماه" value={month.value} suffix={month.unit} />
          </HeroStats>
          {canUseSmsCredit && (
            <HeroBand
              to="/dashboard/sms-credit"
              icon={<IconMessage size={22} stroke={1.7} aria-hidden />}
              label="اعتبار پیامک"
              value={wallet.isLoading ? "…" : wallet.isError ? "دریافت نشد" : rialText(wallet.data?.amount)}
              action={
                <>
                  <IconPlus size={16} stroke={2.2} aria-hidden />
                  افزایش
                </>
              }
            />
          )}
        </HeroCard>
      )}

      <ShortcutGrid shortcuts={shortcuts} badges={badges} onEdit={openEditor} />

      <UpcomingAppointments
        title="نوبت‌های بعدی"
        meta={upcoming.length ? `${faNumber(upcoming.length)} نوبت مانده` : undefined}
        items={upcoming.slice(0, 3)}
        moreText={later > 0 ? `${faNumber(later)} نوبت دیگر امروز` : undefined}
        isLoading={appointments.isLoading}
        isError={appointments.isError}
        onRetry={() => void appointments.refetch()}
        empty={
          visitsToday > 0 ? (
            <UpcomingEmpty
              title="نوبت‌های امروز تمام شد"
              text="برنامه‌ی روزهای بعد را در تقویم ببینید."
              actions={<Link to="/dashboard/calendar" className={classes.softAction}>مشاهده‌ی تقویم</Link>}
            />
          ) : (
            <UpcomingEmpty
              title="امروز نوبتی ثبت نشده"
              text="نوبت را خودتان ثبت کنید، یا کیوآر کد سایت را به مشتری‌ها بدهید تا آنلاین رزرو کنند."
              actions={
                <>
                  <Link to="/dashboard/calendar" className={classes.primaryAction}>ثبت نوبت</Link>
                  <Link to="/dashboard/profile/edit/siteQrCode" className={classes.softAction}>کیوآر کد سایت</Link>
                </>
              }
            />
          )
        }
      />

      <Suggestions title="فرصت‌های امروز" items={opportunities} />

      <ShortcutEditor
        opened={editing}
        onClose={closeEditor}
        available={sections}
        selected={shortcuts}
        onSave={save}
        onReset={reset}
      />
    </div>
  );
}

export default Home;
